import React, { useState, useEffect } from 'react';
import './GeneratedWebsitesPage.css';
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

const GeneratedWebsitesPage = () => {
   const [websites, setWebsites] = useState([]);
   const [loading, setLoading] = useState(true);
   const [searchTerm, setSearchTerm] = useState('');
   const [filterStatus, setFilterStatus] = useState('All');

   const jwt = Cookies.get("jwt");
   const decoded = jwt ? jwtDecode(jwt) : null;
   const gmail = decoded?.email;

   useEffect(() => {
      if (gmail) {
         fetchUserWebsites();
      }
   }, [gmail]);

   const fetchUserWebsites = async () => {
      try {
         const url = process.env.REACT_APP_BACKEND_URL;
         // Fetch user data from backend
         const response = await fetch(`${url}/api/user/email/${gmail}`);

         if (response.ok) {
            const userData = await response.json();
            // Create a website entry from user data
            const userWebsite = {
               id: userData.id,
               name: userData.personalInfo?.name || 'My Website',
               url: `${window.location.origin}/jgrpsercstyshh/${userData.id}`,
               thumbnail: userData.personalInfo?.logo_img || 'https://via.placeholder.com/300x200/f59e0b/ffffff?text=My+Website',
               createdAt: new Date().toISOString().split('T')[0], // Today's date
               status: userData.personalInfo?.payment ? 'Active' : 'Pending',
               category: 'Restaurant'
            };

            setWebsites([userWebsite]);
         }
      } catch (error) {
         console.error('Error fetching websites:', error);
      } finally {
         setLoading(false);
      }
   };

   const handleViewWebsite = (url) => {
      window.open(url, '_blank');
   };

   const handleEditWebsite = (website) => {
      alert(`Editing website: ${website.name}`);
      // Here you would typically navigate to an editor page
   };

   const handleDeleteWebsite = (website) => {
      if (window.confirm(`Are you sure you want to delete ${website.name}?`)) {
         setWebsites(websites.filter(w => w.id !== website.id));
         alert(`Website ${website.name} deleted successfully`);
      }
   };

   const handleCreateWebsite = () => {
      // Navigate to sector selection
      window.location.href = '/sector-selection';
   };

   // Filter websites based on search term and status
   const filteredWebsites = websites.filter(website => {
      const matchesSearch = website.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
         website.category.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'All' || website.status === filterStatus;
      return matchesSearch && matchesStatus;
   });

   // Get unique statuses for filter dropdown
   const statuses = ['All', ...new Set(websites.map(w => w.status))];

   if (loading) {
      return (
         <section className="generated-websites-page">
            <div className="container">
               <div className="page-header">
                  <h1 className="page-title">Your Websites</h1>
                  <p className="page-subtitle">Manage all your generated websites in one place</p>
               </div>
               <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading your websites...</p>
               </div>
            </div>
         </section>
      );
   }

   return (
      <section className="generated-websites-page">
         <div className="container">
            <div className="page-header">
               <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <div>
                     <h1 className="page-title">Your Websites</h1>
                     <p className="page-subtitle">Manage all your generated websites in one place</p>
                  </div>
                  <button
                     onClick={() => window.location.href = '/dashboard'}
                     className="glass-button"
                     style={{ padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                     Next: Dashboard <i className="fas fa-arrow-right"></i>
                  </button>
               </div>
            </div>

            <div className="websites-controls">
               <div className="search-box">
                  <i className="fas fa-search"></i>
                  <input
                     type="text"
                     placeholder="Search websites..."
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                     className="search-input"
                  />
               </div>

               <div className="filter-box">
                  <select
                     value={filterStatus}
                     onChange={(e) => setFilterStatus(e.target.value)}
                     className="filter-select"
                  >
                     {statuses.map(status => (
                        <option key={status} value={status}>{status}</option>
                     ))}
                  </select>
               </div>

               <button
                  className="glass-button create-button"
                  onClick={handleCreateWebsite}
               >
                  <i className="fas fa-plus"></i> Create New Website
               </button>
            </div>

            {filteredWebsites.length === 0 ? (
               <div className="empty-state">
                  <i className="fas fa-globe-americas empty-icon"></i>
                  <h3>No Websites Found</h3>
                  <p>
                     {searchTerm || filterStatus !== 'All'
                        ? 'No websites match your search criteria.'
                        : 'You haven\'t created any websites yet.'}
                  </p>
                  <button
                     className="glass-button create-button large"
                     onClick={handleCreateWebsite}
                  >
                     Create Your First Website
                  </button>
               </div>
            ) : (
               <>
                  <div className="websites-stats">
                     <p>Showing {filteredWebsites.length} of {websites.length} websites</p>
                  </div>

                  <div className="websites-grid">
                     {filteredWebsites.map((website) => (
                        <div key={website.id} className="website-card glass-card">
                           <div className="website-thumbnail-container">
                              <img src={website.thumbnail} alt={website.name} className="website-thumbnail" />
                              <div className={`status-badge ${website.status.toLowerCase()}`}>
                                 {website.status}
                              </div>
                           </div>
                           <div className="website-info">
                              <h3 className="website-name">{website.name}</h3>
                              <p className="website-category">{website.category}</p>
                              <p className="website-url" title={website.url}>
                                 <i className="fas fa-link"></i> {website.url}
                              </p>
                              <p className="website-date">
                                 <i className="fas fa-calendar"></i> Created: {website.createdAt}
                              </p>
                           </div>
                           <div className="website-actions">
                              <button
                                 className="glass-button action-button view-button"
                                 onClick={() => handleViewWebsite(website.url)}
                              >
                                 <i className="fas fa-eye"></i> View
                              </button>
                              <button
                                 className="glass-button action-button edit-button"
                                 onClick={() => handleEditWebsite(website)}
                              >
                                 <i className="fas fa-edit"></i> Edit
                              </button>
                              <button
                                 className="glass-button action-button delete-button"
                                 onClick={() => handleDeleteWebsite(website)}
                              >
                                 <i className="fas fa-trash"></i> Delete
                              </button>
                           </div>
                        </div>
                     ))}
                  </div>
               </>
            )}
         </div>
      </section>
   );
};

export default GeneratedWebsitesPage;